import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import anime from 'animejs/lib/anime.es.js';

/*
  VVS Wave Page Transition
  ────────────────────────
  - Durée: 1200ms entrée + 1200ms sortie (lent, hypnotique)
  - Direction: verticale — monte bas→haut, descend haut→bas
  - Crête: roule horizontalement gauche→droite PENDANT le mouvement vertical
  - Amplitude crête: 120-140px
  - Traînée: amplitude 65%, opacity 0.35, décalée 85ms, zIndex 9998
  - Couleur: #0a1628, reflet cyan rgba(0,200,220,0.2) 2px
  - Easing: easeInCubic entrée / easeOutCubic sortie
  - prefers-reduced-motion: fade 200ms
*/

const DURATION_IN   = 900;
const DURATION_OUT  = 900;
const TRAIL_DELAY   = 85;
const FADE_DURATION = 150;

// SVG dimensions
const SVG_WIDTH     = 1440;
const SVG_HEIGHT    = 900;

// Crest configuration - amplitude 120-140px (using 140)
const CREST_AMPLITUDE = 140;
const BASELINE        = CREST_AMPLITUDE;
const MAIN_PEAK_Y     = 0;
const TRAIL_PEAK_Y    = CREST_AMPLITUDE * 0.35; // 65% of main = 35% from top

// Crest horizontal roll range
const PEAK_START = 120;
const PEAK_END   = SVG_WIDTH - 120;

// Generate smooth wave path with rolling crest
function generateWavePath(peakX, baseline, peakY) {
  // Control points for smooth bezier curve
  const leftCtrl1 = peakX * 0.4;
  const leftCtrl2 = peakX * 0.75;
  const rightCtrl1 = peakX + (SVG_WIDTH - peakX) * 0.25;
  const rightCtrl2 = peakX + (SVG_WIDTH - peakX) * 0.6;
  
  return {
    fill: `M0,${baseline} C${leftCtrl1},${baseline} ${leftCtrl2},${peakY} ${peakX},${peakY} C${rightCtrl1},${peakY} ${rightCtrl2},${baseline} ${SVG_WIDTH},${baseline} L${SVG_WIDTH},${SVG_HEIGHT} L0,${SVG_HEIGHT} Z`,
    crest: `M0,${baseline} C${leftCtrl1},${baseline} ${leftCtrl2},${peakY} ${peakX},${peakY} C${rightCtrl1},${peakY} ${rightCtrl2},${baseline} ${SVG_WIDTH},${baseline}`,
  };
}

export default function PageTransition({ children }) {
  const location = useLocation();
  const [displayChildren, setDisplayChildren] = useState(children);
  const [isFading, setIsFading] = useState(false);
  const waveRef = useRef(null);
  const trailRef = useRef(null);
  const contentRef = useRef(null);
  const prevPath = useRef(location.pathname);
  const animationRef = useRef(null);

  useEffect(() => {
    // Same path - just update children
    if (prevPath.current === location.pathname) {
      setDisplayChildren(children);
      return;
    }

    const wave = waveRef.current;
    const trail = trailRef.current;
    const content = contentRef.current;
    
    if (!wave || !trail) {
      setDisplayChildren(children);
      prevPath.current = location.pathname;
      return;
    }

    const newChildren = children;
    const newPath = location.pathname;

    // ═══════════════════════════════════════════════════════════
    // prefers-reduced-motion: fallback fade 200ms
    // ═══════════════════════════════════════════════════════════
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setIsFading(true);
      
      anime({
        targets: content,
        opacity: [1, 0],
        duration: FADE_DURATION,
        easing: 'easeOutQuad',
        complete: () => {
          setDisplayChildren(newChildren);
          prevPath.current = newPath;
          window.scrollTo(0, 0);
          
          anime({
            targets: content,
            opacity: [0, 1],
            duration: FADE_DURATION,
            easing: 'easeInQuad',
            complete: () => setIsFading(false),
          });
        },
      });
      return;
    }

    // ═══════════════════════════════════════════════════════════
    // Full wave animation
    // ═══════════════════════════════════════════════════════════
    
    // Get SVG elements
    const mainFill = wave.querySelector('#wm-fill');
    const mainCrest = wave.querySelector('#wm-crest');
    const trailFill = trail.querySelector('#wt-fill');
    const trailCrest = trail.querySelector('#wt-crest');

    // Reset to initial state - crest at left position
    const initMain = generateWavePath(PEAK_START, BASELINE, MAIN_PEAK_Y);
    const initTrail = generateWavePath(PEAK_START, BASELINE, TRAIL_PEAK_Y);
    
    if (mainFill) mainFill.setAttribute('d', initMain.fill);
    if (mainCrest) mainCrest.setAttribute('d', initMain.crest);
    if (trailFill) trailFill.setAttribute('d', initTrail.fill);
    if (trailCrest) trailCrest.setAttribute('d', initTrail.crest);

    // Show waves and position at bottom (off-screen)
    wave.style.display = 'block';
    trail.style.display = 'block';
    anime.set(wave, { translateY: '100%' });
    anime.set(trail, { translateY: '100%' });

    // ── PHASE 1: Wave rises from bottom to top ──────────────────
    // Main wave vertical movement
    anime({
      targets: wave,
      translateY: ['100%', '0%'],
      duration: DURATION_IN,
      easing: 'easeInCubic',
      complete: () => {
        // Screen is now covered - swap content
        setDisplayChildren(newChildren);
        prevPath.current = newPath;
        window.scrollTo(0, 0);

        // ── PHASE 2: Wave descends from top to bottom ─────────────
        anime({
          targets: wave,
          translateY: ['0%', '-100%'],
          duration: DURATION_OUT,
          easing: 'easeOutCubic',
          complete: () => {
            wave.style.display = 'none';
            anime.set(wave, { translateY: '100%' });
          },
        });
      },
    });

    // Trail wave vertical movement (delayed)
    anime({
      targets: trail,
      translateY: ['100%', '0%'],
      delay: TRAIL_DELAY,
      duration: DURATION_IN,
      easing: 'easeInCubic',
      complete: () => {
        anime({
          targets: trail,
          translateY: ['0%', '-100%'],
          duration: DURATION_OUT,
          easing: 'easeOutCubic',
          complete: () => {
            trail.style.display = 'none';
            anime.set(trail, { translateY: '100%' });
          },
        });
      },
    });

    // ── Crest rolls left → right during ENTIRE animation ────────
    // Total duration = entry + exit = 2400ms
    const totalDuration = DURATION_IN + DURATION_OUT;
    
    // Main wave crest animation
    const mainPeak = { x: PEAK_START };
    anime({
      targets: mainPeak,
      x: PEAK_END,
      duration: totalDuration,
      easing: 'linear',
      update: () => {
        const paths = generateWavePath(mainPeak.x, BASELINE, MAIN_PEAK_Y);
        if (mainFill) mainFill.setAttribute('d', paths.fill);
        if (mainCrest) mainCrest.setAttribute('d', paths.crest);
      },
    });

    // Trail wave crest animation (delayed)
    const trailPeak = { x: PEAK_START };
    anime({
      targets: trailPeak,
      x: PEAK_END,
      delay: TRAIL_DELAY,
      duration: totalDuration,
      easing: 'linear',
      update: () => {
        const paths = generateWavePath(trailPeak.x, BASELINE, TRAIL_PEAK_Y);
        if (trailFill) trailFill.setAttribute('d', paths.fill);
        if (trailCrest) trailCrest.setAttribute('d', paths.crest);
      },
    });

  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initial SVG paths
  const initMainPaths = generateWavePath(PEAK_START, BASELINE, MAIN_PEAK_Y);
  const initTrailPaths = generateWavePath(PEAK_START, BASELINE, TRAIL_PEAK_Y);

  return (
    <>
      {/* Content wrapper */}
      <div ref={contentRef} style={{ opacity: isFading ? 0 : 1 }}>
        {displayChildren}
      </div>

      {/* Main wave - zIndex 9999 */}
      <div
        ref={waveRef}
        aria-hidden="true"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 9999,
          pointerEvents: 'all',
          display: 'none',
          overflow: 'hidden',
          willChange: 'transform',
        }}
      >
        <svg
          viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ width: '100%', height: '100%', willChange: 'transform' }}
        >
          {/* Wave body */}
          <path id="wm-fill" d={initMainPaths.fill} fill="#0a1628" />
          {/* Cyan crest highlight - 2px max, très subtil */}
          <path
            id="wm-crest"
            d={initMainPaths.crest}
            fill="none"
            stroke="rgba(0,200,220,0.2)"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </div>

      {/* Trail wave - zIndex 9998, opacity 0.35 */}
      <div
        ref={trailRef}
        aria-hidden="true"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 9998,
          pointerEvents: 'none',
          display: 'none',
          overflow: 'hidden',
          willChange: 'transform',
          opacity: 0.35,
        }}
      >
        <svg
          viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ width: '100%', height: '100%', willChange: 'transform' }}
        >
          {/* Trail wave body */}
          <path id="wt-fill" d={initTrailPaths.fill} fill="#0a1628" />
          {/* Trail crest highlight - slightly dimmer */}
          <path
            id="wt-crest"
            d={initTrailPaths.crest}
            fill="none"
            stroke="rgba(0,200,220,0.15)"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </div>
    </>
  );
}
